import { useEffect, useRef, useState } from 'react';
import ValidatedInput from '../../validations/ValidatedInput';
import { categoryApi } from './api';
import './CategoriesScreen.css';

function FileDrop({ label, hint, file, preview, onChange }) {
  const inputRef = useRef(null);
  return (
    <button
      type="button"
      className={`cat-drop${file || preview ? ' is-filled' : ''}`}
      onClick={() => inputRef.current?.click()}
    >
      <span className="cat-drop__label">{label}</span>
      {preview ? (
        <span className="cat-drop__preview">
          <img src={preview} alt="" />
        </span>
      ) : (
        <span className="cat-drop__icon" aria-hidden>
          ↑
        </span>
      )}
      <span className="cat-drop__meta">{file?.name || hint}</span>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        hidden
        onChange={(e) => onChange(e.target.files?.[0] || null)}
      />
    </button>
  );
}

export default function CategoriesScreen({ onMessage }) {
  const flash = (t) => onMessage?.(t);
  const [mains, setMains] = useState([]);
  const [subs, setSubs] = useState([]);
  const [catFile, setCatFile] = useState(null);
  const [preview, setPreview] = useState('');
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [catForm, setCatForm] = useState({ name: '', type: 'main', parent: '', sortOrder: 0 });

  const refresh = async () => {
    const [m, s] = await Promise.all([
      categoryApi.list({ type: 'main', active: 'false' }).catch(() => ({ data: { data: [] } })),
      categoryApi.list({ type: 'sub', active: 'false' }).catch(() => ({ data: { data: [] } })),
    ]);
    setMains(m.data.data || []);
    setSubs(s.data.data || []);
  };

  useEffect(() => {
    refresh().catch(() => flash('Could not load categories'));
  }, []);

  useEffect(() => {
    if (catFile) {
      const url = URL.createObjectURL(catFile);
      setPreview(url);
      return () => URL.revokeObjectURL(url);
    }
    if (editingId) {
      const item = [...mains, ...subs].find((c) => c._id === editingId);
      setPreview(item?.image || '');
      return undefined;
    }
    setPreview('');
    return undefined;
  }, [catFile, editingId, mains, subs]);

  const resetForm = () => {
    setCatForm({ name: '', type: 'main', parent: '', sortOrder: 0 });
    setCatFile(null);
    setEditingId(null);
  };

  const saveCategory = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('name', catForm.name);
      fd.append('type', catForm.type);
      if (catForm.parent) fd.append('parent', catForm.parent);
      fd.append('sortOrder', String(catForm.sortOrder || 0));
      if (catFile) fd.append('image', catFile);
      if (editingId) {
        await categoryApi.update(editingId, fd);
        flash(catFile ? 'Category updated with image' : 'Category updated');
      } else {
        const res = await categoryApi.create(fd);
        const saved = res.data?.data;
        if (catFile && !saved?.image) {
          flash('Category saved, but image did not upload — check server logs');
        } else {
          flash(catFile ? 'Category saved with image' : 'Category saved');
        }
      }
      resetForm();
      await refresh();
    } catch (err) {
      console.error('[categories] save failed', err);
      flash(err.response?.data?.message || err.message || 'Category failed');
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (c) => {
    setEditingId(c._id);
    setCatForm({
      name: c.name || '',
      type: c.type || 'main',
      parent: String(c.parent?._id || c.parent || ''),
      sortOrder: c.sortOrder ?? 0,
    });
    setCatFile(null);
  };

  const removeCategory = async (id) => {
    try {
      await categoryApi.remove(id);
      flash('Category removed');
      await refresh();
    } catch (err) {
      flash(err.response?.data?.message || 'Remove failed');
    }
  };

  const parentName = (sub) => {
    const id = String(sub.parent?._id || sub.parent || '');
    return mains.find((m) => m._id === id)?.name || '—';
  };

  return (
    <div className="cat-screen">
      <form className="cat-screen__panel" onSubmit={saveCategory}>
        <p className="cat-screen__label">{editingId ? 'Edit category' : 'Add category'}</p>

        <div className="cat-screen__toggle" role="group" aria-label="Category type">
          <button
            type="button"
            className={catForm.type === 'main' ? 'is-active' : ''}
            onClick={() => setCatForm((f) => ({ ...f, type: 'main', parent: '' }))}
          >
            Main
          </button>
          <button
            type="button"
            className={catForm.type === 'sub' ? 'is-active' : ''}
            onClick={() => setCatForm((f) => ({ ...f, type: 'sub' }))}
          >
            Sub
          </button>
        </div>
        <p className="cat-screen__note">
          {catForm.type === 'main'
            ? 'Top-level groups like Women, Men, or Kids.'
            : 'Nested groups like Westernwear or Lehengas under a main category.'}
        </p>

        <label className="cat-field">
          <span>Name</span>
          <ValidatedInput
            kind="text"
            name="name"
            value={catForm.name}
            onChange={(e) => setCatForm((f) => ({ ...f, name: e.target.value }))}
            placeholder={catForm.type === 'main' ? 'e.g. Women' : 'e.g. Lehengas'}
            required
          />
        </label>

        {catForm.type === 'sub' && (
          <label className="cat-field">
            <span>Parent main</span>
            <select
              value={catForm.parent}
              onChange={(e) => setCatForm((f) => ({ ...f, parent: e.target.value }))}
              required
            >
              <option value="">Select main category</option>
              {mains.map((c) => (
                <option key={c._id} value={c._id}>
                  {c.name}
                </option>
              ))}
            </select>
          </label>
        )}

        <label className="cat-field">
          <span>Sort order</span>
          <ValidatedInput
            kind="number"
            name="sortOrder"
            value={catForm.sortOrder}
            onChange={(e) => setCatForm((f) => ({ ...f, sortOrder: e.target.value }))}
          />
        </label>

        <FileDrop
          label="Category image"
          hint="Optional cover image"
          file={catFile}
          preview={preview}
          onChange={setCatFile}
        />

        <div className="cat-screen__actions">
          {editingId && (
            <button type="button" className="cat-screen__cancel" onClick={resetForm}>
              Cancel
            </button>
          )}
          <button type="submit" className="cat-screen__save" disabled={saving}>
            {saving ? 'Saving…' : editingId ? 'Update category' : 'Add category'}
          </button>
        </div>
      </form>

      <aside className="cat-screen__aside">
        <div className="cat-list-block">
          <div className="cat-list-block__head">
            <p className="cat-screen__label">Main categories</p>
            <span>{mains.length}</span>
          </div>
          <div className="cat-list">
            {mains.length === 0 && <p className="cat-list__empty">No main categories yet.</p>}
            {mains.map((c) => (
              <article key={c._id} className={`cat-card${editingId === c._id ? ' is-editing' : ''}`}>
                <div
                  className="cat-card__media"
                  style={c.image ? { backgroundImage: `url(${c.image})` } : undefined}
                />
                <div className="cat-card__body">
                  <strong>{c.name}</strong>
                  <span>Main · order {c.sortOrder ?? 0}</span>
                </div>
                <div className="cat-card__actions">
                  <button type="button" className="cat-card__edit" onClick={() => startEdit(c)}>Edit</button>
                  <button type="button" className="cat-card__remove" onClick={() => removeCategory(c._id)}>Remove</button>
                </div>
              </article>
            ))}
          </div>
        </div>

        <div className="cat-list-block">
          <div className="cat-list-block__head">
            <p className="cat-screen__label">Sub categories</p>
            <span>{subs.length}</span>
          </div>
          <div className="cat-list">
            {subs.length === 0 && <p className="cat-list__empty">No sub categories yet.</p>}
            {subs.map((c) => (
              <article key={c._id} className={`cat-card${editingId === c._id ? ' is-editing' : ''}`}>
                <div
                  className="cat-card__media"
                  style={c.image ? { backgroundImage: `url(${c.image})` } : undefined}
                />
                <div className="cat-card__body">
                  <strong>{c.name}</strong>
                  <span>Under {parentName(c)}</span>
                </div>
                <div className="cat-card__actions">
                  <button type="button" className="cat-card__edit" onClick={() => startEdit(c)}>Edit</button>
                  <button type="button" className="cat-card__remove" onClick={() => removeCategory(c._id)}>Remove</button>
                </div>
              </article>
            ))}
          </div>
        </div>
      </aside>
    </div>
  );
}
